import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { actions } from 'darwin_core';

import { States } from '../../../app/src/components/WebSocketClient.js';
import { useAuth } from '../auth/AuthenticationContext.jsx';
import { useWebSocket } from '../transport/WebSocketProvider.jsx';
import { useJsonApi } from '../api/JsonApiProvider.jsx';
import { useDiagnosticsJsonApi } from '../api/DiagnosticsJsonApiProvider.jsx';
import usePrevious from '../hooks/usePrevious.js';

// Re-seed the read model on a WebSocket RECONNECT (never the initial connect —
// StoreBootstrap owns that). Renders nothing; mounted inside the socket provider
// so it can read the connection status.
//
// A browser tab keeps its socket open while backgrounded, so — unlike the mobile
// app, whose OS suspends it and which therefore refetches on foreground — the
// web's only real staleness source is a dropped-and-reopened socket (the API
// Gateway idle timeout, its ~2h connection cap, or a network blip). While
// disconnected the socket misses incremental frames — live telemetry AND the
// desiredState pushes that drive cross-client state (e.g. the Holiday cancel
// lock). On reconnect we re-run the SAME authoritative REST homes fetch the
// initial seed uses (refreshHelloMessage -> jsonApi.findAll('home')), which
// re-ingests the complete homes list plus the inline desiredState blocks, closing
// the gap.
//
// REST — not a WS `hello` — is used deliberately: a hello REPLY can be a filtered
// or partial view, and the device reducer applies a hello as a FULL REPLACE of
// the homes list, so a partial/empty hello would wipe the read model. The REST
// collection is the complete list (same source and trust as the initial seed).
export default function WsResync() {
  const dispatch = useDispatch();
  const { authenticated } = useAuth();
  const wasAuthenticated = usePrevious(authenticated);
  const { jsonApi } = useJsonApi();
  const { diagsApi } = useDiagnosticsJsonApi();
  const ws = useWebSocket();
  const status = ws?.webSocketInfo?.webSocketStatus;
  const prevStatus = usePrevious(status);

  // Latched once the socket has opened this session. The first open is the initial
  // connect (StoreBootstrap already seeded from REST); every open after it is a
  // reconnect that must re-sync. Kept in a ref so it survives re-renders without
  // re-triggering the effect.
  const hasConnectedRef = useRef(false);

  // Re-arm on sign-out. The ref lives for the whole tab (this component is mounted
  // unconditionally), but the "first open is the initial connect" invariant is
  // per-SESSION. Without this reset a second sign-in in the same tab (or a second
  // user on a shared browser) treats its fresh initial connect as a reconnect and
  // re-seeds a second time on top of StoreBootstrap's own seed. Mirrors the
  // seededRef re-arm StoreBootstrap does on the same transition. Sign-out can't be
  // read from the socket status (it goes to Closed, indistinguishable from a
  // network blip), so key it off auth.
  useEffect(() => {
    if (wasAuthenticated && !authenticated) {
      hasConnectedRef.current = false;
    }
  }, [authenticated, wasAuthenticated]);

  useEffect(() => {
    const justOpened = status === States.Open && prevStatus !== States.Open;
    if (!justOpened) {
      return;
    }
    if (!hasConnectedRef.current) {
      hasConnectedRef.current = true;
      return;
    }
    if (jsonApi) {
      dispatch(actions.refreshHelloMessage(jsonApi, diagsApi));
    }
  }, [status, prevStatus, jsonApi, diagsApi, dispatch]);

  return null;
}
